import { Button } from '@progress/kendo-react-buttons';
import React, { useEffect } from 'react';
import { ACTIONS, Controls, EventData, EVENTS, ORIGIN, PartialDeep, STATUS, Step, Styles, useJoyride } from 'react-joyride';
import { useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from '../app/hooks/store-hooks';
import { ROUTES } from '../app/router/routes';
import { setTutorial } from '../app/state/slices/global-slice';
import { removeTutorialSelectedFlight } from '../app/state/slices/workspace-slice';
import allSteps, { FeStep } from './steps';

export function Tutorial() {
    const [step, setStep] = React.useState(0);
    const [run, setRun] = React.useState(false);
    const [steps, setSteps] = React.useState<Step[]>([]);
    const clickedByUser = React.useRef<boolean>(false);
    const canGoNext = React.useRef<boolean>(true);
    const customerAdminRight = useAppSelector((x) => x.user.rights.customerAdmin);
    const tutorial = useAppSelector((x) => x.global.tutorial);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        if (!tutorial) {
            setRun(false);
            dispatch(removeTutorialSelectedFlight());
            return;
        }
        const steps = allSteps[tutorial.scenarioId];
        if (steps) {
            const filtered = steps.filter((s) => (s.data?.requiresCustomerAdmin ? customerAdminRight : true));
            setSteps(filtered);
            setStep(0);
            setRun(true);
            // All tutorial start from home
            navigate(ROUTES.HOME);
        }
    }, [tutorial]);

    const userButtonClicked = React.useCallback(() => {
        clickedByUser.current = true;
        joyride.controls.next();
    }, []);

    const callback = async (data: EventData, controls: Controls) => {
        // waiting for the DOM element to appear
        if (data.waiting) {
            // skip
            return;
        }

        const { action, status, type } = data;
        const step = data.step as FeStep;

        if (
            status === STATUS.FINISHED ||
            status === STATUS.SKIPPED ||
            action === ACTIONS.CLOSE ||
            type === EVENTS.ERROR ||
            type === EVENTS.TARGET_NOT_FOUND
        ) {
            setRun(false);
            dispatch(setTutorial(null));
            return;
        }

        // Before step begins
        if (type === EVENTS.STEP_BEFORE) {
            canGoNext.current = true;
            clickedByUser.current = false;
            if (step.data?.canGoNext) {
                canGoNext.current = await step.data.canGoNext.apply(step);
            }
            if (step.data?.autoClick && canGoNext.current) {
                const element = document.querySelector(step.target as string);
                if (element) {
                    element.addEventListener('click', userButtonClicked);
                }
            }
        }
        // After step completed
        else if (type === EVENTS.STEP_AFTER) {
            if (step.data?.afterNavigate && action === ACTIONS.NEXT) {
                navigate(step.data.afterNavigate);
            }

            if (step.data?.autoClick) {
                const element = document.querySelector(step.target as string);
                if (element) {
                    element.removeEventListener('click', userButtonClicked);
                }
            }

            // Only auto click if 1're moving to next step
            if (action === ACTIONS.NEXT && step.data?.autoClick === true && !clickedByUser.current) {
                document.querySelector<HTMLButtonElement>(data.step.target as string)?.click();
            }

            const add = action === ACTIONS.PREV ? -1 : 1;

            setStep((x) => x + add);
        }
    };

    const styles: PartialDeep<Styles> = {
        tooltipTitle: {
            fontWeight: 600,
            fontSize: '1.2rem'
        },
        tooltipContent: {
            fontWeight: 400,
            fontSize: '1rem'
        },
        arrow: {
            color: 'var(--fe-primary)'
        }
    } as const;

    const joyride = useJoyride({
        run: run,
        styles: styles,
        debug: import.meta.env.DEV,
        onEvent: callback,
        continuous: true,
        stepIndex: step,
        steps: steps,
        options: {
            zIndex: 21000,
            overlayClickAction: false,
            dismissKeyAction: 'close'
        },
        floatingOptions: {
            flipOptions: {
                crossAxis: true,
                mainAxis: true,
                padding: 20,
                boundary: document.documentElement
            },
            shiftOptions: {
                crossAxis: true,
                mainAxis: true,
                padding: 20,
                boundary: document.documentElement
            }
        },
        loaderComponent: () => <></>,
        tooltipComponent: (props) => {
            return (
                <div
                    style={{
                        width: '300px',
                        backgroundColor: 'var(--fe-primary)',
                        color: 'white',
                        borderColor: 'var(--kendo-input-border)',
                        borderWidth: 0,
                        borderRadius: 6,
                        boxShadow: 'none',
                        bottom: props.step?.data?.bottom ?? 'none'
                    }}
                    className="px-3 pt-3 pb-2 k-card">
                    <div style={{ ...styles.tooltipTitle }}>{props.step.title}</div>
                    <div className="pt-1" style={{ ...styles.tooltipContent }}>
                        {props.step.content}
                    </div>
                    <div className="py-2 d-flex flex-row gap-2">
                        <Button
                            fillMode="flat"
                            className="p-0"
                            onClick={() => props.controls.skip()}
                            style={{ textDecoration: 'underline' }}>
                            Dismiss Tutorial
                        </Button>
                        <span className="flex-grow-1"></span>
                        {!(props.step as FeStep).hideBackButton && props.index !== 0 && (
                            <button
                                className="k-button k-button-flat"
                                style={{ padding: 0, width: 18, height: 18 }}
                                onClick={() => props.controls.prev(ORIGIN.BUTTON_BACK)}>
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                                    <path
                                        d="M7.707 11.293l7-7a1 1 0 011.498 1.32l-.084.094L9.83 12l6.292 6.293a1 1 0 01.084 1.32l-.084.094a1 1 0 01-1.32.083l-.094-.083-7-7a1 1 0 010-1.414z"
                                        fillRule="evenodd"
                                        fill="white"></path>
                                </svg>
                            </button>
                        )}
                        <div style={{ color: 'var(--color-neutral-800)' }}>
                            {props.index + 1} of {props.size}
                        </div>
                        <button
                            id="tutorial-next-button"
                            className={`k-button k-button-flat k-rounded-md ${props.step.data?.hideNextButton ? 'd-none' : ''}`}
                            style={{ width: 18, height: 18, padding: 0 }}
                            onClick={() => canGoNext.current && props.controls.next()}>
                            <svg viewBox="0 0 24 24">
                                <path
                                    d="M16.297 12.707l-7 7a1 1 0 01-1.497-1.32l.083-.094L14.175 12 7.883 5.707a1 1 0 01-.083-1.32l.083-.094a1 1 0 011.32-.083l.094.083 7 7a1 1 0 010 1.414z"
                                    fillRule="evenodd"
                                    fill="white"></path>
                            </svg>
                        </button>
                    </div>
                </div>
            );
        }
    });
    return joyride.Tour;
}
